import type { TabType } from '@gov-design-system-ce/components';
import { GovAccordion } from '@gov-design-system-ce/react';
import type { CmsFaqCategory } from '@gov-nx/api';
import { MarkdownRender } from '@gov-nx/component/web';
import { LocalizeNameSpaceTypes, useLocale } from '@gov-nx/core/service';
import { isNumber } from '@gov-nx/core/types';
import { AccordionItem, Button, LayoutGap, Tabs } from '@gov-nx/ui/web';
import { useBoolean } from '@gov-nx/utils/common';

interface ExpandableFaqCategoryProps {
	category: CmsFaqCategory;
	maxFaqPerTab?: number;
	isToggleButtonVisible: boolean;
}

const ExpandableFaqCategory = ({
	category,
	maxFaqPerTab,
	isToggleButtonVisible,
}: ExpandableFaqCategoryProps) => {
	const showAll = useBoolean(!maxFaqPerTab);
	const faq = category.faqs ?? [];
	const showToggleButton =
		isToggleButtonVisible &&
		isNumber(maxFaqPerTab) &&
		faq.length > maxFaqPerTab;
	const visibleFaqs = showAll.isTrue ? faq : faq.slice(0, maxFaqPerTab);
	const { t } = useLocale(LocalizeNameSpaceTypes.Page);

	return (
		<>
			<GovAccordion size="m">
				{visibleFaqs.map((accordion) => (
					<AccordionItem
						key={accordion.id}
						label={<h3>{accordion.question}</h3>}>
						<MarkdownRender>{accordion.answer ?? ''}</MarkdownRender>
					</AccordionItem>
				))}
			</GovAccordion>
			{showToggleButton ? (
				<Button
					onClick={showAll.toggle}
					color="primary"
					type="outlined"
					size="m">
					{showAll.value
						? t('button.zobrazit-mene')
						: t('button.zobrazit-vice')}
				</Button>
			) : null}
		</>
	);
};

export interface CmsFaqTabsProps {
	faqCategories: CmsFaqCategory[];
	maxFaqPerTab?: number;
	isToggleButtonVisible?: boolean;
	isLoading: boolean;
	tabsType: TabType;
}

export const CmsFaqTabs = ({
	faqCategories,
	maxFaqPerTab,
	isToggleButtonVisible = false,
	isLoading,
	tabsType,
}: CmsFaqTabsProps) => {
	if (isLoading) return null;

	return (
		<LayoutGap>
			{faqCategories.length > 0 && (
				<Tabs
					color="primary"
					size="m"
					items={faqCategories.map((category) => ({
						label: category.label as string,
						children: (
							<ExpandableFaqCategory
								category={category}
								maxFaqPerTab={maxFaqPerTab}
								isToggleButtonVisible={isToggleButtonVisible}
							/>
						),
					}))}
					type={tabsType}
				/>
			)}
		</LayoutGap>
	);
};
